import { useMemo } from 'react'

import 'reactflow/dist/style.css'

import { Node } from 'dagre'
import { Edge } from 'reactflow'

import { ICompanyProvidersResponseDTO } from '../../../sdk/company/company.types'
import { ProviderNode } from '../components/ProviderNode/provider-node.comp'

const getInitialData = (
  providers: ICompanyProvidersResponseDTO[],
  onNodeExpand?: (id: string, direction?: 'next' | 'previous') => void,
  expandedPreviousNodes: string[] = [],
  expandedNextNodes: string[] = [],
) => {
  const initialNodes: Node<any>[] = []
  providers.forEach((provider) => {
    initialNodes.push({
      id: provider.id,
      position: { x: 0, y: 0 },
      data: {
        id: provider.id,
        provider: provider.name,
        address: provider.address,
        segment: provider.segment,
        status: provider.status,
        taxId: provider.taxId,
        hasNextItems: provider.hasNextItems,
        expandedNext: expandedNextNodes.includes(provider.id),
        hasPreviousItems: provider.hasPreviousItems,
        expandedPrevious: expandedPreviousNodes.includes(provider.id),
        onNodeExpand,
      },
      type: 'default',
    })
  })

  const initialEdges: Edge<any>[] = []
  providers.forEach((item) => {
    item.nextItems?.forEach((nextItem) => {
      initialEdges.push({
        id: `edge-$${item.id}-${nextItem}`,
        source: item.id,
        target: nextItem,
        animated: true,
        style: {
          stroke: '#c8c8c8',
          strokeWidth: 2,
        },
      })
    })
  })

  return {
    nodes: initialNodes,
    edges: initialEdges,
  }
}

interface IUseProviderTree {
  providers?: ICompanyProvidersResponseDTO[]
  onNodeExpand?: (nodeId: string, direction?: 'next' | 'previous') => void
  expandedPreviousNodes?: string[]
  expandedNextNodes?: string[]
}

export const useProviderTree = ({
  providers,
  onNodeExpand,
  expandedPreviousNodes = [],
  expandedNextNodes = [],
}: IUseProviderTree) => {
  const initialData = useMemo(() => {
    if (providers) {
      return getInitialData(
        providers,
        onNodeExpand,
        expandedPreviousNodes,
        expandedNextNodes,
      )
    }
    return {
      nodes: [],
      edges: [],
    }
  }, [providers])

  const { nodes: initialNodes, edges: initialEdges } = initialData

  // According to the documentation this optimizes the rendering of nodes
  const nodeTypes = useMemo(() => ({ default: ProviderNode }), [])

  return {
    initialNodes,
    initialEdges,
    nodeTypes,
  }
}
